import { useTranslation } from '@mukil/i18n';
import { Button } from '@jumpstone/ui-react';
import { format_date } from '../../lib/format';
import {
  essInviteActionFor,
  type EssEmployeeStatus,
  type EssInvite,
} from '../../data/wagepro_store_ess_invites';
import { EssInviteActionLabel, EssStatusBadge } from './ess_status_badge';

/**
 * The directory's Self-service cell: status badge plus a one-line "when"
 * (expiry of a live invite, when a dead one expired, or since when access
 * has been live). The row's invite action (`EmployeeEssActionButton`) is
 * rendered beside it by the caller, so the mobile card can show status
 * alone. `status: null` means the invite list hasn't resolved -- render a
 * quiet dash rather than guessing "Not invited".
 */
export function EmployeeEssStatusCell({
  status,
  invite,
}: {
  status: EssEmployeeStatus | null;
  invite: EssInvite | undefined;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  if (status === null) {
    return <span className="text-[color:var(--ink-muted)]">{'—'}</span>;
  }
  const when = ess_when_line(status, invite);
  return (
    <span className="inline-flex flex-col items-start gap-1">
      <EssStatusBadge status={status} />
      {when !== null && (
        <span className="text-[11.5px] leading-none text-[color:var(--ink-muted)]">
          {when === 'expires'
            ? t('employees.ess_expires_on', {
                date: invite_date(invite, 'expires_at', locale),
              })
            : when === 'expired'
              ? t('employees.ess_expired_on', {
                  date: invite_date(invite, 'expires_at', locale),
                })
              : t('employees.ess_since', {
                  date: invite_date(invite, 'accepted_at', locale),
                })}
        </span>
      )}
    </span>
  );
}

function invite_date(
  invite: EssInvite | undefined,
  field: 'expires_at' | 'accepted_at',
  locale: string,
): string {
  const value = invite?.[field];
  return value ? format_date(value, locale) : '';
}

function ess_when_line(
  status: EssEmployeeStatus,
  invite: EssInvite | undefined,
): 'expires' | 'expired' | 'since' | null {
  if (invite === undefined) return null;
  if (status === 'pending') return 'expires';
  if (status === 'expired') return 'expired';
  if (status === 'linked' && invite.accepted_at !== null) return 'since';
  return null;
}

/**
 * The one invite action a row's status allows (`essInviteActionFor`) --
 * Send invite / Resend invite / Re-invite -- at the same size and weight as
 * the row's "View" button. Renders nothing when there is no action
 * (accepted, no email) or no write access.
 */
export function EmployeeEssActionButton({
  status,
  busy,
  disabled,
  onSendInvite,
}: {
  status: EssEmployeeStatus | null;
  busy: boolean;
  /** Another row's invite is in flight — one mint at a time. */
  disabled: boolean;
  /** `undefined` hides the action (read-only grant / list not ready). */
  onSendInvite: (() => void) | undefined;
}): React.JSX.Element | null {
  const action = status === null ? null : essInviteActionFor(status);
  if (action === null || !onSendInvite) return null;
  return (
    <Button
      variant="secondary"
      size="xs"
      loading={busy}
      disabled={disabled}
      onClick={onSendInvite}
      data-testid="directory-ess-action"
    >
      <span className="inline-flex items-center gap-1.5">
        {action === 'resend' ? (
          <ResendIcon className="h-3.5 w-3.5" />
        ) : (
          <MailIcon className="h-3.5 w-3.5" />
        )}
        <EssInviteActionLabel action={action} />
      </span>
    </Button>
  );
}

function MailIcon({ className }: { className?: string }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function ResendIcon({ className }: { className?: string }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}
